import React from 'react';

import { InputBase, TableHead, MenuItem } from '@material-ui/core';
import { AnswerOption } from 'models/reviewQuestion';
import styled from 'styled-components';
import { Divider1, FlexContainer } from 'styles/common';

import { HappyEmojiIcon, SmartEmojiIcon, VeryHappyEmojiIcon, SadEmojiIcon, AngryEmojiIcon } from 'constants/icons';
import theme from 'constants/theme';

import { Typography10, Typography4 } from 'uiToolkit/Typography';

import CommentsCheckboxes from './commentsCheckboxes';
import { Table, TableRow, TableCell, Dash, StyledSelect } from './styles';

const OPTIONS_COUNT = [3, 4, 5];

const EMOJI_ICONS = {
  5: <VeryHappyEmojiIcon size="fixed20px" />,
  4: <HappyEmojiIcon size="fixed20px" />,
  3: <SmartEmojiIcon size="fixed20px" />,
  2: <SadEmojiIcon size="fixed20px" />,
  1: <AngryEmojiIcon size="fixed20px" />,
};

const EmojiAnswerType = ({ question, updateQuestion, source, isDuplicating }) => {
  const { answerOptions } = question;

  const handleChange = (param, value) => updateQuestion(param, value);

  const handleMaxOptionsChange = (maxOptions) => {
    const options = [...answerOptions];
    if (maxOptions < answerOptions.length) {
      for (let i = 0; i < answerOptions.length - maxOptions; i += 1) {
        options.pop();
      }
    } else {
      // clear description for last element
      options[options.length - 1].description = '';
      for (let i = 0; i < maxOptions - answerOptions.length; i += 1) {
        options.push(AnswerOption.fromJSON({ score: answerOptions.length + i + 1 }));
      }
    }
    updateQuestion('answerOptions', options);
  };

  const handleAnswerOptionsChange = (index, param, value) => {
    const data = [
      ...answerOptions.slice(0, index),
      { ...answerOptions[index], [param]: value || '' },
      ...answerOptions.slice(index + 1),
    ];
    updateQuestion('answerOptions', data);
  };

  const getEmojiArrayIndex = (index) => {
    let arr = [1, 2, 3, 4, 5];
    if (answerOptions.length === 4) {
      arr = [1, 2, 4, 5];
    } else if (answerOptions.length === 3) {
      arr = [1, 3, 5];
    }
    return arr[index];
  };

  return (
    <Container>
      <FlexContainer style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography4 customColor={theme.palette.text.dark1} topGutter={1}>
          Max Value
        </Typography4>
        <StyledSelect
          value={question.answerOptions.length}
          displayEmpty
          variant="outlined"
          onChange={(e) => handleMaxOptionsChange(e.target.value)}
          width="45%"
          disabled={source === 'engagement' && question?.isEditable === false && !isDuplicating}
        >
          {OPTIONS_COUNT.map((emojiCount) => (
            <StyledMenuItem primary value={emojiCount}>
              <Typography4>{emojiCount}</Typography4>
            </StyledMenuItem>
          ))}
        </StyledSelect>
      </FlexContainer>
      <Divider1 topGutter={3} bottomGutter={3} />
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <Typography10 customColor={theme.palette.text.dark1} align="left" topGutter={0.5} bottomGutter={0.5}>
                Emoji
              </Typography10>
            </TableCell>
            <TableCell>
              <Typography10 customColor={theme.palette.text.dark1} align="left" topGutter={0.5} bottomGutter={0.5}>
                Description
              </Typography10>
            </TableCell>
            <TableCell>
              <Typography10 customColor={theme.palette.text.dark1} align="left" topGutter={0.5} bottomGutter={0.5}>
                Score
              </Typography10>
            </TableCell>
          </TableRow>
        </TableHead>
        {answerOptions &&
          answerOptions.map((option, i) => {
            const isStartOrEndOption = i === 0 || i === answerOptions.length - 1;
            return (
              <TableRow>
                <TableCell style={{ width: '40px' }}>{EMOJI_ICONS[getEmojiArrayIndex(i)]}</TableCell>
                <TableCell>
                  {isStartOrEndOption ? (
                    <StyledInputBase
                      type="text"
                      value={option.description}
                      placeholder="Add description"
                      onChange={(e) => handleAnswerOptionsChange(i, 'description', e.target.value)}
                      disabled={source === 'engagement' && question?.isEditable === false && !isDuplicating}
                    />
                  ) : (
                    <Dash />
                  )}
                </TableCell>
                <TableCell style={{ width: '75px' }}>
                  <StyledInputBase type="number" value={option.score} placeholder="Score" readOnly />
                </TableCell>
              </TableRow>
            );
          })}
      </Table>
      <CommentsCheckboxes
        question={question}
        handleQuestionChange={handleChange}
        source={source}
        isDuplicating={isDuplicating}
      />
    </Container>
  );
};

const Container = styled.div`
  margin-top: 2rem;
`;

const StyledMenuItem = styled(MenuItem)`
  padding-left: 24px;
  padding-right: 24px;
  &:hover {
    background: none;
    .MuiTouchRipple-root {
      background-color: rgba(0, 0, 0, 0.04);
    }
  }

  &.Mui-selected {
    background: none;
    &:hover {
      background: none;
    }
    .MuiTouchRipple-root {
      background-color: rgba(0, 0, 0, 0.08);
    }
  }
`;

const StyledInputBase = styled(InputBase)`
  width: 100%;
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type='number'] {
    -moz-appearance: textfield;
    text-align: center;
    font-weight: 600;
  }
  input {
    color: #333;
  }
  ${({ disabled }) =>
    disabled &&
    `
    pointer-events: none;
    opacity: 0;
  `};
`;

export default EmojiAnswerType;
